import { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useReferredUsers, formatCents } from '@/services/referrals';
import { ReferredUser, ReferredUserStatus } from '@/models/referral';

const STATUSES: Array<{ value: string; label: string }> = [
    { value: 'all', label: 'All statuses' },
    { value: 'signed_up', label: 'Signed up' },
    { value: 'pending', label: 'Pending' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'churned', label: 'Churned' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'voided', label: 'Voided' },
];

const PAGE_SIZE = 50;

/**
 * System-wide list of every referred user. Cursor-paginated via the opaque
 * `nextCursor`; "Load more" appends each page rather than replacing it. The
 * status filter / search reset the accumulated pages (new query key).
 */
const ReferredUsersTab = () => {
    const [status, setStatus] = useState('all');
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [pages, setPages] = useState<ReferredUser[][]>([]);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    // Reset accumulated pages whenever the filter/search changes.
    useEffect(() => {
        setCursor(undefined);
        setPages([]);
    }, [status, debounced]);

    const { data, isLoading, isFetching } = useReferredUsers({
        status,
        cursor,
        limit: PAGE_SIZE,
        search: debounced || undefined,
    });

    // Append each freshly-fetched page (keyed by cursor) to the accumulator.
    useEffect(() => {
        if (data?.status === 'error' || !data?.data) return;
        const page = data.data.referredUsers || [];
        setPages((prev) => {
            const index = cursor ? prev.length : 0;
            const next = cursor ? [...prev] : [];
            next[index] = page;
            return next;
        });
    }, [data, cursor]);

    const users = useMemo(() => pages.flat(), [pages]);
    const nextCursor = data?.data?.nextCursor ?? null;
    const isError = data?.status === 'error';
    // First load = fetching with nothing accumulated yet.
    const isInitialLoading = (isLoading || isFetching) && users.length === 0 && !isError;

    return (
        <div>
            <div className="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <select className="form-select" style={{ width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                            {s.label}
                        </option>
                    ))}
                </select>
                <input
                    type="search"
                    className="form-control"
                    style={{ maxWidth: 360 }}
                    placeholder="Search by email, referrer, or code…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <span className="text-muted ms-auto">{users.length} loaded</span>
            </div>

            {isError ? (
                <div className="alert alert-danger">{data?.error?.message || 'Failed to load referred users.'}</div>
            ) : isInitialLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr>
                                    <th>Referred user</th>
                                    <th>Status</th>
                                    <th>Referrer</th>
                                    <th>Code</th>
                                    <th>Signed up</th>
                                    <th>Plan</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.referredUserId}>
                                        <td>
                                            <div>{u.referredEmailMasked}</div>
                                            <div className="text-muted small font-monospace">{u.referredUserId}</div>
                                        </td>
                                        <td>
                                            <span className={`badge text-bg-${statusColor(u.status)}`}>{statusLabel(u.status)}</span>
                                        </td>
                                        <td>
                                            <div>{u.referrerEmail || <span className="font-monospace small">{u.referrerId}</span>}</div>
                                            {u.referrerEmail && <div className="text-muted small font-monospace">{u.referrerId}</div>}
                                        </td>
                                        <td className="font-monospace">{u.code || '—'}</td>
                                        <td className="small">{u.signupAt ? new Date(u.signupAt).toLocaleString() : '—'}</td>
                                        <td className="small">
                                            {u.plan ? (
                                                <>
                                                    {u.plan}
                                                    {u.planInterval ? ` / ${u.planInterval}` : ''}
                                                    {typeof u.lastPurchaseAmountCents === 'number' && (
                                                        <div className="text-muted">{formatCents(u.lastPurchaseAmountCents, u.currency)}</div>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {users.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">
                                            No referred users yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {nextCursor && (
                        <div className="d-flex justify-content-center mt-3">
                            <button type="button" className="btn btn-outline-secondary" disabled={isFetching} onClick={() => setCursor(nextCursor)}>
                                {isFetching ? 'Loading…' : 'Load more'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const statusLabel = (status: ReferredUserStatus): string => {
    switch (status) {
        case 'signed_up':
            return 'Signed up';
        default:
            return status.charAt(0).toUpperCase() + status.slice(1);
    }
};

const statusColor = (status: ReferredUserStatus): string => {
    switch (status) {
        case 'qualified':
            return 'success';
        case 'pending':
            return 'warning';
        case 'signed_up':
            return 'info';
        case 'churned':
            return 'danger';
        case 'refunded':
            return 'danger';
        case 'voided':
            return 'secondary';
        default:
            return 'secondary';
    }
};

export default ReferredUsersTab;
